import React, { Fragment } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import {
  t,
  isEmeaRegion,
  getPriceSavingsInformation,
} from '@insight/toolkit-utils';
import Tooltip from '../Tooltip/Tooltip';
import { default as Currency } from '../Currency/Currency';
import { default as ListPrice } from '../ListPrice/ListPrice';
import { connectToLocale } from '../Locale/Locale';

export const Pricing = ({
  abbreviation,
  contractDiscount,
  currencyCode,
  displayDiscount,
  displayListPrice,
  hasContractSection,
  isLoggedIn,
  isIPSUser,
  listPrice,
  webPrice,
  priceInclVat,
  showPrice,
  showSaving,
}) => {
  if (!showPrice) return null;
  const showVAT = isEmeaRegion();
  const priceLabelRightMargin = isEmeaRegion()
    ? 'u-margin-right-large'
    : 'u-margin-right-tiny';
  const commonGridClasses =
    'o-grid__item u-1/1 o-grid__item--shrink u-margin-top-tiny';
  const showDiscount = (isIPSUser && displayDiscount) || false;
  const discount = contractDiscount || 0;
  const isOpenMarket =
    isLoggedIn && isIPSUser && hasContractSection && !abbreviation;
  const insightPrice = webPrice;
  const nListPrice = listPrice ? Number(listPrice) : 0;
  const nInsightPrice = insightPrice ? Number(insightPrice) : 0;

  const { priceSavings, priceSavingsInPercentage } = getPriceSavingsInformation(
    nListPrice,
    nInsightPrice
  );
  const discountClassName =
    showDiscount && priceSavings > 0 ? 'u-text-bold' : '';

  const trimDescription = (text, length) => {
    const data =
      text && text.length > length
        ? `${text.substring(0, length)}...`
        : text || '-';
    return data;
  };

  const renderPricingLabel = () => (
    <div
      className={`o-grid__item o-grid__item--shrink u-margin-top-tiny ${priceLabelRightMargin}`}
    >
      <span className="u-margin-bot-small u-text-bold">{t('Your price:')}</span>
    </div>
  );

  return (
    <div
      className={cn('o-grid u-1/1', {
        'c-pdp__pricing-container__vat': showVAT,
      })}
    >
      <div className="o-grid__item u-1/1 c-pdp__contractName">
        {isIPSUser && hasContractSection && abbreviation && (
          <Tooltip content={abbreviation} className="c-tooltip__carousel">
            <span className="c-text" style={{ webkitBoxOrient: 'vertical' }}>
              {trimDescription(abbreviation, 50)}
            </span>
          </Tooltip>
        )}
        {isOpenMarket && <span>{t('Open Market')}</span>}
      </div>
      {displayListPrice && (
        <ListPrice
          discount={discount}
          showDiscount={showDiscount}
          labelClassName={`${commonGridClasses} u-2/5@tablet u-text-bold`}
          valueClassName={`${commonGridClasses} u-3/5@tablet`}
          listPrice={listPrice?.toString()}
          insightPrice={webPrice?.toString()}
          currencyCode={currencyCode}
          showListPriceLabel
          tax={false}
          showHorizontalSaveTag
          showSaving={false}
          showVAT={showVAT}
          strike
        />
      )}
      <div className="o-grid">
        {renderPricingLabel()}
        <div
          className={`${showVAT ? '' : 'o-grid'} o-grid__item u-margin-top-tiny`}
        >
          <Currency
            className={cn('c-pdp__customer-price u-margin-right-tiny', {
              'c-pdp__customer-price-excl': showVAT,
            })}
            currencyCode={currencyCode}
            value={webPrice}
            tax={false}
            showVAT={showVAT}
          />
          {showVAT && (
            <div>
              <Currency
                className="c-pdp__customer-price"
                currencyCode={currencyCode}
                value={priceInclVat}
                tax
                showVAT={showVAT}
              />
            </div>
          )}
          {displayListPrice && showSaving && priceSavings > 0 ? (
            <div className={cn('c-list-price__save', discountClassName)}>
              {t('Save')}{' '}
              <Currency value={priceSavings} currencyCode={currencyCode} />
              {showDiscount &&
                priceSavings > 0 &&
                ` (${hasContractSection ? t('Contract Discount:') : t('Discount:')} ${priceSavingsInPercentage}%)`}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

Pricing.propTypes = {
  displayListPrice: PropTypes.bool,
  isIPSUser: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  listPrice: PropTypes.number,
  unitPrice: PropTypes.number.isRequired,
  webPrice: PropTypes.number,
  priceInclVat: PropTypes.number,
  showPrice: PropTypes.bool,
  showSaving: PropTypes.bool,
  currencyCode: PropTypes.string,
};

Pricing.defaultProps = {
  displayListPrice: false,
  isLoggedIn: false,
  isIPSUser: false,
  showPrice: false,
  showSaving: false,
};

export default connectToLocale(Pricing);
